import { DERIVATION_API, INGESTION_API, fetchApi, postApi, putApi, deleteApi } from '@/api/client'
import type { EventMapping, EventRule, EntityColRule } from '../types'

// ── Event Mappings ────────────────────────────────────────────────────────────

export const listEventMappings = (accId?: number): Promise<EventMapping[]> =>
  fetchApi(`${DERIVATION_API}/event-mappings${accId ? `?accId=${accId}` : ''}`)

export const createEventMapping = (data: EventMapping): Promise<EventMapping> =>
  postApi(`${DERIVATION_API}/event-mappings`, data)

export const updateEventMapping = (
  accId: number,
  eventType: string,
  category: string,
  eventName: string,
  data: EventMapping,
): Promise<EventMapping> =>
  putApi(`${DERIVATION_API}/event-mappings/${accId}/${eventType}/${category}/${eventName}`, data)

export const deleteEventMapping = (
  accId: number,
  eventType: string,
  category: string,
  eventName: string,
): Promise<void> =>
  deleteApi(`${DERIVATION_API}/event-mappings/${accId}/${eventType}/${category}/${eventName}`)

// ── Event Rules ───────────────────────────────────────────────────────────────

export const listEventRules = (accId?: number): Promise<EventRule[]> =>
  fetchApi(`${DERIVATION_API}/event-rules${accId ? `?accId=${accId}` : ''}`)

export const createEventRule = (data: EventRule): Promise<EventRule> =>
  postApi(`${DERIVATION_API}/event-rules`, data)

export const updateEventRule = (
  accId: number | null,
  eventType: string,
  category: string,
  eventName: string,
  data: EventRule,
): Promise<EventRule> =>
  putApi(`${DERIVATION_API}/event-rules/${accId ?? 'null'}/${eventType}/${category}/${eventName}`, data)

export const deleteEventRule = (
  accId: number | null,
  eventType: string,
  category: string,
  eventName: string,
): Promise<void> =>
  deleteApi(`${DERIVATION_API}/event-rules/${accId ?? 'null'}/${eventType}/${category}/${eventName}`)

// ── Entity Column Rules ───────────────────────────────────────────────────────

export const listEntityColRules = (accId?: number): Promise<EntityColRule[]> =>
  fetchApi(`${DERIVATION_API}/entity-col-rules${accId ? `?accId=${accId}` : ''}`)

export const createEntityColRule = (data: EntityColRule): Promise<EntityColRule> =>
  postApi(`${DERIVATION_API}/entity-col-rules`, data)

export const updateEntityColRule = (
  accId: number | null,
  name: string,
  data: EntityColRule,
): Promise<EntityColRule> =>
  putApi(`${DERIVATION_API}/entity-col-rules?accId=${accId ?? 'null'}&name=${encodeURIComponent(name)}`, data)

export const deleteEntityColRule = (
  accId: number | null,
  name: string,
): Promise<void> =>
  deleteApi(`${DERIVATION_API}/entity-col-rules?accId=${accId ?? 'null'}&name=${encodeURIComponent(name)}`)

// ── Entity Schema (for template creation UI) ──────────────────────────────────
// Pulls from the ingestion API's system_schemas — same source as data-ingestion Step 3.

export const listAllSchemaFields = async (): Promise<{ entity: string; field: string }[]> => {
  const { entities } = await fetchApi<{ entities: string[] }>(`${INGESTION_API}/schemas/list`)

  const grouped = await Promise.all(
    entities.map(async entity => {
      const { fields = [] } = await fetchApi<{ fields?: { name: string }[] }>(
        `${INGESTION_API}/schemas/${entity}`,
      )
      return fields.map(f => ({ entity, field: f.name }))
    }),
  )

  return grouped
    .flat()
    .filter(f => f.field)
    .sort((a, b) => a.entity.localeCompare(b.entity) || a.field.localeCompare(b.field))
}
